import { useCallback, useEffect, useRef, useState } from "react";

/* export type WSStatus = "idle" | "connecting" | "open" | "closed";
type WSReadyState = "pending" | "ready" | "failed"; */

/* type UseWebSocketProps = {
  joinEvent: string; // "joinSurvey"
  joinData: string; // `${projectId}:${userId}`
  joinServerEvent: string // "surveyJoined"
  onMessage?: (msg: string) => void; // incoming message (stringified)
}; */

const heartbeatMs = 45000; // ping every N ms (default: 45s)
const handshakeTimeoutMs = 8000;
const maxBackoffMs = 30000; // cap reconnect backoff (default: 30s)

const base_WS_URL = process.env.REACT_APP_WS_URL;
const base_WS_URL_Test =
  "ws://bulldog-perfect-kindly.ngrok-free.app/ws";

export function useWebSocket({
  joinEvent,
  joinData,
  joinServerEvent,
  onMessage,
}) {
  const wsRef = useRef(null);
  const hbTimer = useRef(null);
  const openingLock = useRef(false); // StrictMode-safe: avoid parallel opens
  const handshakeTimer = useRef(null);

  const queueRef = useRef([]);
  const reconnectTimer = useRef(null);

  const url = base_WS_URL;

  const [status, setStatus] = useState("idle");
  const [readyState, setReadyState] = useState("pending");
  const [lastError, setLastError] = useState(null);

  const clearHandshakeTimer = () => {
    if (handshakeTimer.current) {
      window.clearTimeout(handshakeTimer.current);
      handshakeTimer.current = null;
    }
  };

  const clearReconnect = () => {
    if (reconnectTimer.current) {
      window.clearTimeout(reconnectTimer.current);
      reconnectTimer.current = null;
    }
  };

  const send = useCallback((msg) => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    ws.send(typeof msg === "string" ? msg : JSON.stringify(msg));
  }, []);

  const stopHeartbeat = () => {
    if (hbTimer.current) {
      window.clearInterval(hbTimer.current);
      hbTimer.current = null;
    }
  };

  const startHeartbeat = useCallback(() => {
    stopHeartbeat();
    hbTimer.current = window.setInterval(() => {
      send({ event: "ping", data: Date.now() }); // server can ignore or respond with {"event":"pong"}.
    }, heartbeatMs);
  }, [send, heartbeatMs]);

  const flushQueue = () => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    while (queueRef.current.length) {
      ws.send(queueRef.current.shift());
    }
  };

  const close = useCallback(() => {
    clearHandshakeTimer();
    stopHeartbeat();
    openingLock.current = false;

    const ws = wsRef.current;
    wsRef.current = null;

    if (
      ws &&
      (ws.readyState === WebSocket.OPEN ||
        ws.readyState === WebSocket.CONNECTING)
    ) {
      ws.close(1000, "component unmount");
    }

    console.log('WS closed')
    setStatus("closed");
    setReadyState("pending");
  }, []);

  const open = useCallback(() => {
    if (openingLock.current) return;

    const rs = wsRef.current?.readyState;
    if (rs === WebSocket.OPEN || rs === WebSocket.CONNECTING) return;

    openingLock.current = true;
    setLastError(null);
    setStatus("connecting");
    setReadyState("pending");

    const ws = new WebSocket(url);
    wsRef.current = ws;

    // if we never get surveyJoined then failed
    clearHandshakeTimer();
    handshakeTimer.current = window.setTimeout(() => {
      setReadyState("failed");
      setLastError("Handshake timeout (surveyJoined not received)");
    }, handshakeTimeoutMs);

    ws.addEventListener("open", () => {
      setStatus("open");
      // send join
      send({ event: joinEvent, data: joinData });
      startHeartbeat();
    });

    ws.addEventListener("message", (e) => {
      const raw = typeof e.data === "string" ? e.data : String(e.data);
      onMessage?.(raw);

      try {
        const parsed = JSON.parse(raw);

        // join ack → ready
        if (
          joinServerEvent &&
          parsed?.event === joinServerEvent
        ) {
          clearHandshakeTimer();
          setReadyState("ready");
        }

      } catch {
        // ignore invalid JSON
      }
    });

    ws.addEventListener("error", () => {
      setLastError("WebSocket error");
    });

    ws.addEventListener("close", () => {
      clearHandshakeTimer();
      stopHeartbeat();
      openingLock.current = false;
      setStatus("closed");
    });
  }, [joinData, onMessage, send]);

  const reconnect = useCallback(() => {
    close();
    open();
  }, [close, open]);

  // connect on mount, disconnect on unmount
  useEffect(() => {
    open();
    return () => close();
  }, [open, close]);

  return {
    status,
    readyState, // pending | ready | failed
    lastError,
    send,
  };
}
